/**
 * What a grammar exercise 语法练习 says to a reader, in the words the writing
 * drill uses (`./xiezi-copy.ts`).
 *
 * Checked answers use Correct 对 or OK 还行. Incomplete arrangements and
 * wrong gap answers use Incorrect 不对. Unverified orders get neutral
 * feedback. The right sentence is shown whole under
 * the line, so the line never repeats it. Hugh found the first wording, such
 * as "That is the word." and "None came back.", stilted, and the lines here
 * were rewritten with Codex on 2026-10-15.
 */

/**
 * The line over the items saying where the reader is.
 *
 * @param done - How many items have been answered right.
 * @param total - How many items the exercise has.
 * @param returning - How many missed items are still to come back.
 * @returns The line.
 */
export function progressLine(
  done: number,
  total: number,
  returning: number,
): string {
  const place = `${String(done)} of ${String(total)} done.`;

  return returning === 0
    ? place
    : `${place} ${String(returning)} to try again.`;
}

/** The line once every item has been answered right. */
export function finishedLine(total: number): string {
  return `Exercise complete. All ${String(total)} questions answered.`;
}

/** What is said about a gap filled right. */
export const gapRightLine = "Correct 对";

/**
 * What is said about a gap filled with the word in the other writing, which
 * the exercise writes the way the sentence does.
 */
export function gapOtherWritingLine(word: string): string {
  return `OK 还行. This exercise uses ${word}.`;
}

/** What is said about a gap filled wrong. */
export function gapWrongLine(word: string): string {
  return `Incorrect 不对. The missing word is ${word}.`;
}

/** What is said about an empty gap. */
export const gapEmptyLine = "Type the missing word first.";

/** What is said about the words put in the sentence's own order. */
export const orderRightLine = "Correct 对";

/** What is said about a complete order outside the checked answers. */
export const orderUnverifiedLine =
  "This word order hasn't been checked. Compare it with the checked example below.";

/** What is said about an answer that comes back later in the sitting. */
const incorrectLine =
  "Incorrect 不对. Compare your answer with the checked example below.";

/** What is said about missing, repeated or extra tiles. */
export const orderWrongLine = incorrectLine;

/** What is said about an empty line. */
export const orderEmptyLine = "Put the words on the line first.";

/** What is said about the right sentence picked. */
export const chooseRightLine = "Correct 对";

/** What is said about a wrong sentence picked. */
export const chooseWrongLine = incorrectLine;

/** What is said about a Choose item answered with nothing picked. */
export const chooseEmptyLine = "Pick a sentence first.";

/** What is said about a rewrite that is on the list. */
export const rewriteRightLine = "Correct 对";

/** What is said about a rewrite on the list in the other writing. */
export const rewriteOtherLine = "OK 还行. Your answer uses the other script.";

/** What is said about a rewrite that is not on the list. */
export const rewriteWrongLine = incorrectLine;

/** What is said about a Transform item answered with nothing typed. */
export const rewriteEmptyLine = "Type the sentence first.";

/**
 * What the finish line adds once the finish has handed the sentences to
 * 复习, for a signed-in reader.
 */
export const handedOverLine = "The sentences are in your Review.";
